import {
  DarkTheme,
  DefaultTheme,
  ThemeProvider as NavThemeProvider,
} from '@react-navigation/native'
import { QueryClientProvider } from '@tanstack/react-query'
import { useFonts } from 'expo-font'
import { Stack, router as expoRouter, usePathname, useRouter, useSegments } from 'expo-router'
import * as SplashScreen from 'expo-splash-screen'
import { useEffect, useRef, useState } from 'react'
import { Animated, Easing, Linking, Platform, View } from 'react-native'
import { GestureHandlerRootView } from 'react-native-gesture-handler'
import { SafeAreaProvider } from 'react-native-safe-area-context'
import { BreakpointProvider } from '@/lib/hooks/use-breakpoint'

import NetInfo from '@react-native-community/netinfo'
import { onlineManager } from '@tanstack/react-query'
import {
  useSentryNavigationTracking,
  setSentryUser,
  withSentry,
  Sentry,
  captureException as captureSentryException,
} from '../lib/utils/sentry'
import { deepLinkPathFromUrl } from '@/lib/utils/deep-link-path'
import { ToastContainer } from './ui/Toast'
import { ErrorBoundary as AppErrorBoundary } from './ui/ErrorBoundary'
import { TagoThemeProvider } from './ThemeProvider'
import { ThemeBootstrap } from './ThemeBootstrap'
import { ThemedStatusBar } from './ThemedStatusBar'
import { OfflineBanner } from './ui/OfflineBanner'
import { ConfigErrorScreen } from './ui/ConfigErrorScreen'
import { MandatoryUpdateScreen } from './ui/MandatoryUpdateScreen'
import { AccountRestrictedScreen } from './ui/AccountRestrictedScreen'
import { useThemeStore } from '../lib/stores/theme.store'
import { useUIStore } from '../lib/stores/ui.store'
import { DURATION, useSemanticColors } from '../lib/theme/tokens'
import { env } from '../lib/env'

import '../global.css'
import '../lib/i18n/config'

import { queryClient } from '../lib/query-client'
import { useAuth, authKeys } from '../lib/hooks/use-auth'
import { useAuthStore } from '../lib/stores/auth.store'
import { useBiometric } from '../lib/hooks/use-biometric'
import { resolveBiometricUnlockGate } from '../lib/auth/biometric-unlock-gate'
import { isPasswordRecoveryPath } from '../lib/auth/password-recovery-session'
import { BiometricStore } from '../lib/storage/secure-storage'
import { COLORS } from '../lib/brand'
import { usePushNotifications } from '../lib/hooks/use-push-notifications'
import { useRealtimeReconnect } from '../lib/hooks/use-realtime-reconnect'
import { useMandatoryUpdateGate } from '../lib/hooks/use-mandatory-update-gate'
import { useOtaUpdate } from '../lib/hooks/use-ota-update'
import { OtaUpdateController } from './updates/OtaUpdateController'
import { MandatoryReviewGate } from './review/MandatoryReviewGate'
import { useStoreUpdatePrompt } from '../lib/hooks/use-store-update-prompt'
import { isIsolatedPreviewBuild } from '../lib/runtime-build'
import { supabase } from '../lib/supabase'
import { authService } from '../lib/services/auth.service'
import { securityTelemetryService } from '../lib/services/security-telemetry.service'
import { productTelemetryService } from '../lib/services/product-telemetry.service'
import { stableStackOptions } from '../lib/navigation/stable-stack-options'
import { getContactVerificationRequirement } from '../lib/utils/account-contact'
import { getRestrictionUntilMs, isProfileRestricted } from '../lib/utils/account-status'
import { addBreadcrumb } from '../lib/utils/observability'
import { isAnonymousPreviewRoute } from '../lib/navigation/preview-anonymous-route'
import { previewAnonymousRedirectTarget } from '../lib/navigation/preview-anonymous-landing'
import { isWebAuthPath, isWebCredentialPath } from '../lib/navigation/web-auth-route'
import { useTranslation } from 'react-i18next'
import { GateFallback } from './ui/GateFallback'
import { StartupSurface, STARTUP_SURFACE } from './ui/StartupSurface'

if (__DEV__) console.log('[Tago] Root layout module loaded')

const FONT_LOAD_TIMEOUT_MS = 7000
/**
 * Ceiling on the OS biometric prompt.
 *
 * Generous on purpose: this is a human deciding whether to present a finger or
 * a face, and the prompt itself has no deadline. It is not a UX timer, it is a
 * liveness backstop for a native promise that never settles — see the gate in
 * `checkBiometric`, where an unsettled promise leaves the app behind a
 * full-screen touch-absorbing splash forever.
 */
const BIOMETRIC_PROMPT_TIMEOUT_MS = 60_000
const LAUNCH_SURFACE = STARTUP_SURFACE
type TagoWebWindow = Window & {
  __TAGO_BOOT_PATH?: string
  __TAGO_BOOT_PATHNAME?: string
}

/**
 * Races the native biometric prompt against a deadline so the startup gate
 * always resolves. Same shape as `fetchPolicyWithTimeout` in
 * `lib/hooks/use-mandatory-update-gate.ts`. Rejecting is correct: the caller's
 * catch treats it exactly like a hardware failure and requires a password.
 */
async function withBiometricTimeout(promise: Promise<boolean>): Promise<boolean> {
  let timeout: ReturnType<typeof setTimeout> | null = null
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timeout = setTimeout(
          () => reject(new Error('Biometric prompt timed out')),
          BIOMETRIC_PROMPT_TIMEOUT_MS,
        )
      }),
    ])
  } finally {
    if (timeout) clearTimeout(timeout)
  }
}

function LaunchHandoffFrame() {
  return <StartupSurface animateMark={false} />
}

function RootLayout() {
  useSentryNavigationTracking()
  const [fontGateTimedOut, setFontGateTimedOut] = useState(false)
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme)
  const rootColors = useSemanticColors(resolvedTheme === 'dark')
  const rootBackground = rootColors.background

  const [fontsLoaded, fontError] = useFonts({
    SpaceMono: require('../assets/fonts/SpaceMono-Regular.ttf'),
    Heavitas: require('../assets/fonts/Heavitas.ttf'),
    // Static Montserrat weights — variable font renders too thin on Android.
    // Keep only runtime families referenced by brand/theme tokens.
    'Montserrat-SemiBold': require('../assets/fonts/Montserrat-SemiBold.ttf'),
    'Montserrat-Bold': require('../assets/fonts/Montserrat-Bold.ttf'),
  })

  useEffect(() => {
    if (__DEV__) console.log('[Tago] RootLayout mounted, fontsLoaded:', fontsLoaded)
    // Font load failures should never strand production behind a blank native
    // window. Capture the signal, then continue with platform fallback fonts.
    // On web, expo-font wraps fontfaceobserver which can spuriously time out at 6 s
    // even when the @font-face rule is injected and the asset URL resolves 200 (we've
    // verified both via curl + the bundle output). Browsers swap to the @font-face
    // family naturally once the file lands in `document.fonts`; blocking render on
    // a polling probe just punishes users with a blank screen → ErrorBoundary crash.
    if (fontError && Platform.OS !== 'web') {
      Sentry.captureException(fontError, {
        tags: { screen: 'root', action: 'font-load' },
      })
      setFontGateTimedOut(true)
    }
    if (fontError && __DEV__)
      console.warn('[Tago] Font load failure (non-fatal on web):', fontError)
  }, [fontError, fontsLoaded])

  useEffect(() => {
    if (Platform.OS === 'web' || fontsLoaded || fontGateTimedOut) return undefined

    const timeout = setTimeout(() => {
      addBreadcrumb('startup', 'Font load timed out; rendering app with fallback fonts', {
        timeoutMs: FONT_LOAD_TIMEOUT_MS,
      })
      setFontGateTimedOut(true)
    }, FONT_LOAD_TIMEOUT_MS)

    return () => clearTimeout(timeout)
  }, [fontGateTimedOut, fontsLoaded])

  // Web: inject focus-visible rings for keyboard navigation (not mouse clicks)
  useEffect(() => {
    if (Platform.OS !== 'web') return
    const style = document.createElement('style')
    style.textContent = `
      *:focus { outline: none; }
      *:focus-visible { outline: 2px solid ${COLORS.coral.DEFAULT}; outline-offset: 2px; border-radius: 8px; }
    `
    document.head.appendChild(style)
    return () => {
      document.head.removeChild(style)
    }
  }, [])

  // Web maps import Mapbox GL's stylesheet directly from the package, so Metro
  // emits it in the app's own CSS bundle. The old runtime /vendor injection
  // (MapLibre) is gone with the provider.

  // Web OAuth callback: Supabase redirects Google OAuth back with ?code=...
  // Because the shared client disables detectSessionInUrl for native safety,
  // web must explicitly exchange the PKCE code and then remove auth params.
  useEffect(() => {
    if (Platform.OS !== 'web') return
    if (typeof window === 'undefined') return

    const url = new URL(window.location.href)
    // Password recovery owns this one-use grant. The OAuth boot callback used
    // to exchange it a second time and navigate away from the password form.
    if (isPasswordRecoveryPath(url.pathname)) return
    const code = url.searchParams.get('code')
    const errorDescription = url.searchParams.get('error_description')

    if (errorDescription && __DEV__) {
      console.warn('[Tago] Google OAuth returned an error:', errorDescription)
    }

    if (errorDescription && !code) {
      window.sessionStorage.removeItem('tago.oauth.register_intent')
      useAuthStore.getState().setCompletingSignup(false)
      void import('../lib/toast').then(({ toast }) => {
        toast.error(errorDescription)
      })
      url.searchParams.delete('error')
      url.searchParams.delete('error_code')
      url.searchParams.delete('error_description')
      window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`)
      return
    }

    if (!code) return

    const hadRegisterIntent = window.sessionStorage.getItem('tago.oauth.register_intent') === '1'
    useAuthStore.getState().setCompletingSignup(true)

    void supabase.auth
      .exchangeCodeForSession(code)
      .then(async ({ data, error }) => {
        if (error) throw error
        if (data.session) {
          queryClient.setQueryData(['auth', 'session'], data.session)
          useAuthStore.getState().markCredentialUnlock()
        }
        if (hadRegisterIntent && data.session?.user.id) {
          try {
            await authService.completeSignup(data.session.user.id, false)
          } catch (consentErr) {
            captureSentryException(consentErr)
          }
        }
        const requirement = getContactVerificationRequirement(data.session?.user ?? null, null)
        const route =
          requirement === 'email'
            ? '/(auth)/verify-email'
            : requirement === 'phone'
              ? '/(auth)/verify-phone'
              : '/(tabs)/explore'
        expoRouter.replace(route as never)
      })
      .catch((error: unknown) => {
        captureSentryException(error)
        if (__DEV__) console.error('[Tago] OAuth callback failed:', error)
        const message =
          error && typeof error === 'object' && 'message' in error
            ? String((error as { message?: unknown }).message ?? '')
            : ''
        void import('../lib/toast').then(({ toast }) => {
          toast.error(message || 'OAuth failed')
        })
      })
      .finally(() => {
        window.sessionStorage.removeItem('tago.oauth.register_intent')
        setTimeout(() => useAuthStore.getState().setCompletingSignup(false), 0)
        url.searchParams.delete('code')
        url.searchParams.delete('state')
        url.searchParams.delete('error')
        url.searchParams.delete('error_code')
        url.searchParams.delete('error_description')
        window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`)
      })
  }, [])

  // Global unhandled promise rejection handler — capture in Sentry
  useEffect(() => {
    const handler = (event: PromiseRejectionEvent) => {
      // Keep abort/offline/provider-expected failures as breadcrumbs. Directly
      // calling the SDK here bypassed the shared operational classifier and
      // turned cancelled startup/network work into TAGO-MOBILE-9 noise.
      captureSentryException(event.reason ?? new Error('Unhandled promise rejection'))
    }
    if (typeof globalThis.addEventListener === 'function') {
      globalThis.addEventListener('unhandledrejection', handler)
      return () => globalThis.removeEventListener('unhandledrejection', handler)
    }
    return undefined
  }, [])

  /*
   * El estado de red, con UNA sola derivación para todo el mundo.
   *
   * Esto alimentaba solo a `useUIStore` (que pinta el banner de "sin
   * conexión"), y `onlineManager` de TanStack Query quedaba SIN CABLEAR. Sin
   * él, TanStack asume que siempre hay red: cada mutación se dispara y falla en
   * vez de quedar en pausa hasta que vuelva la señal.
   *
   * Eso obliga a cada pantalla a inventarse su propia red de seguridad, y es la
   * causa de fondo del defecto que tuvo la compuerta de reseña: sin señal, el
   * envío fallaba y la hoja obligatoria se quedaba encima, encerrando a la
   * persona. Ahí lo parcheé donde dolía; acá está el origen.
   *
   * `isConnected ?? true` FALLA ABIERTO a propósito, y por eso se reusa el
   * mismo valor en vez de preguntar aparte: `isInternetReachable` da falsos
   * negativos en Android, y un falso "sin red" pausaría el envío de una entrega
   * — mucho peor que el problema que resuelve. Una sola lectura, dos
   * consumidores.
   */
  useEffect(() => {
    const unsubscribe = NetInfo.addEventListener((state) => {
      const enLinea = state.isConnected ?? true
      useUIStore.getState().setOnline(enLinea)
      onlineManager.setOnline(enLinea)
    })
    return unsubscribe
  }, [])

  // Native: gate on fonts so Text never flashes in the system family before the
  // custom face is ready (FOUT looks broken at our typography scale). Web: the
  // browser handles font swap progressively via the injected @font-face rules,
  // and gating here means a full-screen blank if expo-font's fontfaceobserver
  // times out — a UX cost that doesn't buy anything since the swap is automatic.
  if (!fontsLoaded && Platform.OS !== 'web' && !fontGateTimedOut) {
    return <LaunchHandoffFrame />
  }

  return (
    <GestureHandlerRootView style={{ flex: 1, backgroundColor: rootBackground }}>
      <SafeAreaProvider style={{ flex: 1, backgroundColor: rootBackground }}>
        {/*
          UNA sola respuesta de breakpoint para toda la app.

          `useBreakpoint` tenia estado por instancia y sus 36 llamantes podian
          discrepar -- y discrepaban: al volver atras desde la ficha de un carro
          el DOM quedaba con la barra de escritorio y la de pestanas movil a la
          vez, y explorar en blanco. Va aca arriba, envolviendo todo, para que
          esa discrepancia deje de ser posible por construccion.
        */}
        <BreakpointProvider>
          <QueryClientProvider client={queryClient}>
            <TagoThemeProvider>
              <ThemeBootstrap />
              <ThemedStatusBar />
              <AppErrorBoundary
                onError={(error, errorInfo) => {
                  Sentry.captureException(error, {
                    extra: { componentStack: errorInfo.componentStack },
                  })
                }}
              >
                <OfflineBanner />
                <AuthGate />
              </AppErrorBoundary>
            </TagoThemeProvider>
          </QueryClientProvider>
        </BreakpointProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  )
}

function AuthGate() {
  const { t } = useTranslation()
  const {
    isAuthenticated,
    isInitializing,
    isLoading: isAuthLoading,
    isSessionLoading,
    signOut,
    user,
    profile,
  } = useAuth()
  const { authenticate } = useBiometric()
  const updateGate = useMandatoryUpdateGate()
  useStoreUpdatePrompt(updateGate)
  const otaUpdate = useOtaUpdate(updateGate.status !== 'store-required')
  usePushNotifications()
  // AppState-aware Supabase Realtime reconnect — fixes WebSocket drops on
  // app resume (supabase/realtime-js#463). Singleton: lives in the root
  // layout so screen mounts/unmounts don't create duplicate AppState
  // listeners.
  useRealtimeReconnect()
  const isCompletingSignup = useAuthStore((s) => s.isCompletingSignup)
  const setCompletingSignup = useAuthStore((s) => s.setCompletingSignup)
  const credentialUnlockNonce = useAuthStore((s) => s.credentialUnlockNonce)
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme)
  const gateColors = useSemanticColors(resolvedTheme === 'dark')
  const rootBackground = gateColors.background
  const appContentOpacity = useRef(new Animated.Value(0)).current
  const appContentAnimationStarted = useRef(false)
  const segments = useSegments()
  const pathname = usePathname()
  const router = useRouter()
  // The path the app was opened at, on native.
  //
  // Web captures this into `window.__TAGO_BOOT_PATHNAME` before the bundle
  // runs; native had no equivalent, so `webDeepLinkPath` below was hardcoded
  // to null off-web and the deep link was simply lost. Measured on a Pixel 10:
  // `tago://trips`, `tago://profile/settings` and
  // `tago://hosting/new-listing/details` all cold-started onto Explore,
  // because the boot URL is replaced by the welcome redirect while the session
  // is still resolving and nothing remembers where the user was going. Push
  // notifications and shared links are exactly this path.
  const [nativeBootPath, setNativeBootPath] = useState<string | null>(null)
  // A running Android/iOS app receives later intents through the `url` event,
  // not getInitialURL(). Keep the latest valid route until the same auth,
  // biometric and contact gates that protect a cold launch can release it.
  // Otherwise tapping a trip notification while already browsing Explore is a
  // silent no-op — the precise failure that makes handover operations feel
  // randomly unavailable in the field.
  const [nativeInboundPath, setNativeInboundPath] = useState<string | null>(null)
  // Do not let the auth fallback choose Explore until Android/iOS has handed
  // over its launch intent. Without this small readiness barrier, a warm
  // session can redirect from `/` before `getInitialURL()` resolves, leaving
  // a valid cold-start link stranded on Explore.
  const [nativeInitialUrlResolved, setNativeInitialUrlResolved] = useState(Platform.OS === 'web')
  useEffect(() => {
    if (Platform.OS === 'web') return
    let cancelled = false
    const captureInitialUrl = (url: string | null) => {
      if (cancelled) return
      if (url) setNativeBootPath(deepLinkPathFromUrl(url))
      setNativeInitialUrlResolved(true)
    }
    void Linking.getInitialURL().then(captureInitialUrl, () => captureInitialUrl(null))
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (Platform.OS === 'web') return undefined

    const subscription = Linking.addEventListener('url', ({ url }) => {
      const path = deepLinkPathFromUrl(url)
      if (path) setNativeInboundPath(path)
    })

    return () => subscription.remove()
  }, [])

  const bootPathname =
    Platform.OS === 'web' && typeof window !== 'undefined'
      ? ((window as TagoWebWindow).__TAGO_BOOT_PATHNAME ?? null)
      : null
  const bootPath =
    Platform.OS === 'web' && typeof window !== 'undefined'
      ? ((window as TagoWebWindow).__TAGO_BOOT_PATH ?? bootPathname)
      : null
  const browserPathname =
    Platform.OS === 'web' && typeof window !== 'undefined' ? window.location.pathname : pathname

  const [biometricChecked, setBiometricChecked] = useState(false)
  const [biometricPassed, setBiometricPassed] = useState(false)
  // The app must not expose navigation until its initial auth, biometric and
  // deep-link decisions are settled. We keep the same static handoff frame as
  // native startup instead of layering a Reanimated full-screen overlay above
  // real content: an aesthetic animation must never be able to trap the app.
  const [authReady, setAuthReady] = useState(false)
  const [restrictionClockMs, setRestrictionClockMs] = useState(() => Date.now())
  const initStartTime = useRef(Date.now())
  const nativeSplashHidden = useRef(false)
  const lastAuthRedirectRef = useRef<string | null>(null)
  const lastContactRequirementTelemetryRef = useRef<string | null>(null)

  const routeSegments = segments as readonly string[]
  const firstSegment = routeSegments[0]
  const secondSegment = routeSegments[1]
  const inAuthGroup = firstSegment === '(auth)'
  const inPublicGroup =
    firstSegment === 'legal' ||
    firstSegment === 'split' ||
    firstSegment === 'guardian' ||
    (isIsolatedPreviewBuild && firstSegment === 'preview') ||
    isIsolatedPreviewBuild
  const isBrowserAuthPath = Platform.OS !== 'web' || isWebAuthPath(browserPathname)
  // On web, Expo Router can briefly report the public pathname before it
  // restores the `(auth)` route group. Preview used that transient state to
  // redirect `/register` to discovery, which silently made fixture sign-up
  // impossible. The public credential URL is authoritative for this narrow
  // set of screens; it never classifies booking, payment, or operations as
  // anonymous.
  const isActualAuthRoute =
    (inAuthGroup && isBrowserAuthPath) ||
    (Platform.OS === 'web' && isWebCredentialPath(browserPathname))
  // Renamed in spirit — this is the boot deep link on either platform. The
  // variable keeps its name so the restore branch below reads unchanged.
  const webDeepLinkPath =
    Platform.OS === 'web'
      ? bootPathname && bootPathname !== '/' && !isWebAuthPath(bootPathname)
        ? (bootPath ?? bootPathname)
        : null
      : nativeBootPath && nativeBootPath !== '/' && !isWebAuthPath(nativeBootPath)
        ? nativeBootPath
        : null
  const isBareAuthEntry = inAuthGroup && !secondSegment
  const isPasswordRecoveryStep =
    (inAuthGroup && secondSegment === 'reset-password') ||
    (Platform.OS === 'web' && isPasswordRecoveryPath(browserPathname))
  const isPostSignupAuthStep =
    inAuthGroup &&
    (secondSegment === 'verify-phone' ||
      secondSegment === 'phone-verify' ||
      secondSegment === 'verify-email')
  const accountRestricted =
    isAuthenticated && biometricPassed && isProfileRestricted(profile, restrictionClockMs)
  // Never unmount the auth stack for profile hydration — that wiped mid-OTP
  // state and re-armed auto-send. Profile is not required for contact routing.
  const profilePending =
    isAuthenticated && isAuthLoading && !profile && !inAuthGroup && !inPublicGroup
  const contactRequirement =
    isAuthenticated && !accountRestricted ? getContactVerificationRequirement(user, profile) : null
  // Entry rule (product + account-contact.ts): ONE verified contact is enough
  // — either phone OR a real confirmed email. Phone remains mandatory before
  // booking; that gate lives on the booking path, not app entry.
  //
  // verify-phone accepts SUPPORTED_PHONE_COUNTRY_ISO2S (CA + US/CA/MX).
  const requiredContactRoute =
    contactRequirement === 'email'
      ? '/(auth)/verify-email'
      : contactRequirement === 'phone'
        ? '/(auth)/verify-phone'
        : null
  const shouldRestoreWebDeepLink =
    Boolean(webDeepLinkPath) &&
    isAuthenticated &&
    biometricPassed &&
    !accountRestricted &&
    !requiredContactRoute &&
    (pathname === '/' || isActualAuthRoute)
  const isRequiredContactStep =
    inAuthGroup &&
    ((contactRequirement === 'email' && secondSegment === 'verify-email') ||
      (contactRequirement === 'phone' && secondSegment === 'verify-phone'))
  const shouldHoldForContactVerification =
    isAuthenticated &&
    biometricPassed &&
    !isCompletingSignup &&
    Boolean(requiredContactRoute) &&
    !isRequiredContactStep &&
    !isPasswordRecoveryStep

  // Expo Router handles initial route resolution itself, but it does not retain
  // a warm native intent while our auth gate is deciding whether the session is
  // usable. Consume it exactly once only after every protected prerequisite is
  // satisfied. An unauthenticated user keeps the intended destination through
  // login instead of landing at Explore and having to find the trip manually.
  useEffect(() => {
    if (
      Platform.OS === 'web' ||
      !nativeInboundPath ||
      isInitializing ||
      isSessionLoading ||
      !biometricChecked ||
      !nativeInitialUrlResolved ||
      !isAuthenticated ||
      !biometricPassed ||
      accountRestricted ||
      requiredContactRoute ||
      isCompletingSignup
    ) {
      return
    }

    const target = nativeInboundPath
    setNativeInboundPath(null)
    router.replace(target as never)
  }, [
    accountRestricted,
    biometricChecked,
    biometricPassed,
    isAuthenticated,
    isCompletingSignup,
    isInitializing,
    isSessionLoading,
    nativeInboundPath,
    nativeInitialUrlResolved,
    requiredContactRoute,
    router,
  ])

  // Un tap de notificación en frío se etapa en el store y se consume AQUÍ, una
  // sola vez, con los mismos prerrequisitos que un deep link nativo. Antes el
  // hook hacía router.push directo mientras el gate de auth no había decidido y
  // el redirect del root reemplazaba la ruta: TODAS las notificaciones
  // aterrizaban en explore.
  const pendingNotificationRoute = useAuthStore((s) => s.pendingNotificationRoute)
  const setPendingNotificationRoute = useAuthStore((s) => s.setPendingNotificationRoute)
  useEffect(() => {
    if (
      Platform.OS === 'web' ||
      !pendingNotificationRoute ||
      isInitializing ||
      isSessionLoading ||
      !biometricChecked ||
      !nativeInitialUrlResolved ||
      !isAuthenticated ||
      !biometricPassed ||
      accountRestricted ||
      requiredContactRoute ||
      isCompletingSignup
    ) {
      return
    }

    const target = pendingNotificationRoute
    setPendingNotificationRoute(null)
    router.push(target as never)
  }, [
    accountRestricted,
    biometricChecked,
    biometricPassed,
    isAuthenticated,
    isCompletingSignup,
    isInitializing,
    isSessionLoading,
    nativeInitialUrlResolved,
    pendingNotificationRoute,
    requiredContactRoute,
    router,
    setPendingNotificationRoute,
  ])

  useEffect(() => productTelemetryService.startLifecycle(), [])

  const restrictionUntilMs = getRestrictionUntilMs(profile)

  useEffect(() => {
    if (!restrictionUntilMs) return undefined

    const remainingMs = restrictionUntilMs - Date.now()
    if (remainingMs <= 0) {
      setRestrictionClockMs(Date.now())
      return undefined
    }

    const timer = setTimeout(
      () => setRestrictionClockMs(Date.now()),
      Math.min(remainingMs + 1000, 2147483647),
    )

    return () => clearTimeout(timer)
  }, [restrictionUntilMs])

  useEffect(() => {
    productTelemetryService.recordScreenView(pathname, {
      authenticated: isAuthenticated,
      authInitializing: isInitializing || isSessionLoading,
    })
  }, [isAuthenticated, isInitializing, isSessionLoading, pathname])

  useEffect(() => {
    if (!__DEV__) return
    console.log('[Tago] AuthGate state:', {
      isInitializing,
      isAuthenticated,
      biometricChecked,
      biometricPassed,
      authReady,
      isSessionLoading,
      pathname,
      browserPathname,
      bootPathname,
      nativeBootPath,
      nativeInboundPath,
      nativeInitialUrlResolved,
      elapsed: Date.now() - initStartTime.current,
    })
  }, [
    isInitializing,
    isSessionLoading,
    isAuthenticated,
    biometricChecked,
    biometricPassed,
    authReady,
    pathname,
    browserPathname,
    bootPathname,
    nativeBootPath,
    nativeInboundPath,
    nativeInitialUrlResolved,
  ])

  // Sentry user identification — track across sessions for error attribution
  useEffect(() => {
    if (user) {
      setSentryUser({ id: user.id, email: user.email ?? undefined })
    } else {
      setSentryUser(null)
    }
  }, [user])

  // Keep the native splash over the startup gates until the first real route
  // has committed. This makes the handoff atomic: no white frame, no blank
  // navigator, and no "Cargando..." screen flashing between the OS surface and
  // the interactive app. The animation is one bounded pass; it never remains
  // above an interactive route.
  useEffect(() => {
    if (!authReady || appContentAnimationStarted.current) return undefined
    appContentAnimationStarted.current = true

    const animation = Animated.timing(appContentOpacity, {
      toValue: 1,
      duration: DURATION.fast,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    })
    animation.start()

    // Start the native fade only after the route tree has mounted and the
    // content reveal has begun. On Android this prevents the system window from
    // exposing an uncommitted React frame during the handoff.
    const hideFrame = requestAnimationFrame(() => {
      if (nativeSplashHidden.current) return
      nativeSplashHidden.current = true
      void SplashScreen.hideAsync().catch((error) => {
        if (__DEV__) console.warn('[Tago] Failed to hide native splash:', error)
      })
    })

    return () => {
      animation.stop()
      cancelAnimationFrame(hideFrame)
    }
  }, [appContentOpacity, authReady])

  // Stable ref for authenticate to prevent re-triggering the effect
  const authenticateRef = useRef(authenticate)
  authenticateRef.current = authenticate

  // Biometric gate — single module-level owner (`resolveBiometricUnlockGate`).
  // A component ref used to race: effect deps change while isEnabled() awaits,
  // or AuthGate remounts, and a second authenticateAsync() opens. Credential
  // unlock is checked BEFORE the OS prompt so password/OTP/OAuth never stacks
  // a fingerprint on top of a proof the user just gave.
  const lastBiometricUserIdRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (isInitializing || isSessionLoading) return

    const userId = user?.id ?? null
    // Already mirrored into React state for this identity (or logged-out).
    if (biometricChecked && lastBiometricUserIdRef.current === userId) return

    let cancelled = false
    const checkBiometric = async () => {
      try {
        await resolveBiometricUnlockGate({
          userId: isAuthenticated ? userId : null,
          getCredentialUnlockNonce: () => useAuthStore.getState().credentialUnlockNonce,
          isBiometricEnabled: () => BiometricStore.isEnabled(),
          authenticate: () => withBiometricTimeout(authenticateRef.current()),
        })
        if (cancelled) return
        lastBiometricUserIdRef.current = isAuthenticated ? userId : null
        setBiometricChecked(true)
        // Fail-open: bio is a convenience lock, not a second hard login.
        setBiometricPassed(true)
      } catch (error) {
        // Gate itself fail-opens; this catch is for unexpected throws only.
        if (!cancelled) {
          if (__DEV__) console.warn('[Tago] Biometric gate error — session retained', error)
          lastBiometricUserIdRef.current = isAuthenticated ? userId : null
          setBiometricChecked(true)
          setBiometricPassed(true)
        }
      }
    }

    void checkBiometric()
    return () => {
      cancelled = true
    }
  }, [isInitializing, isSessionLoading, isAuthenticated, biometricChecked, user?.id])

  // If a user falls back to a real credential after a biometric cancel/failure,
  // trust that credential for this running app session. Without this handoff,
  // the root gate keeps `biometricPassed=false` and immediately bounces the
  // user back to login even after a successful password/OTP/OAuth sign-in.
  useEffect(() => {
    if (!isAuthenticated || credentialUnlockNonce === 0) return
    setBiometricChecked(true)
    setBiometricPassed(true)
  }, [credentialUnlockNonce, isAuthenticated])

  // Auth routing + native splash dismissal
  useEffect(() => {
    if (isInitializing || isSessionLoading || !biometricChecked || !nativeInitialUrlResolved) return
    if (contactRequirement && user?.id) {
      const telemetryKey = `${user.id}:${contactRequirement}`
      if (lastContactRequirementTelemetryRef.current !== telemetryKey) {
        lastContactRequirementTelemetryRef.current = telemetryKey
        securityTelemetryService.recordContactVerificationEvent({
          event: 'contact_verification_required',
          userId: user.id,
          contactType: contactRequirement,
          flow: contactRequirement === 'email' ? 'email_link' : 'phone_change',
          channel: contactRequirement === 'email' ? 'email' : 'sms',
        })
      }
    } else {
      lastContactRequirementTelemetryRef.current = null
    }

    // Biometric failed or hardware error — force password re-authentication.
    // Login (not welcome) is correct here: the user is already known, they
    // just need to re-prove identity.
    let redirectTarget: string | null = null

    // La guarda de handoff se baja cuando la sesión YA está viva, no en un
    // temporizador.
    //
    // Diez llamadas repartidas en login, register, phone-login, phone-verify,
    // verify-phone, welcome y auth/callback
    // la bajaban con `setTimeout(() => setCompletingSignup(false), 0)` justo
    // después de `router.replace`. Eso es una carrera: el replace ya sacó al
    // usuario del grupo de auth, pero `isAuthenticated` todavía no propagó
    // desde el store. Con la guarda abajo y la sesión sin propagar, este mismo
    // efecto toma la rama de no-autenticado y manda a `/(auth)/welcome`. Eso es
    // el "inicio sesión, me bota a welcome, entro otra vez y ahí sí caigo en
    // explore": la segunda vez funciona porque la sesión ya está en caché.
    //
    // Bajarla acá, contra el estado real, arregla las diez de una vez y evita
    // la once. Se hace ANTES de calcular el destino para que este mismo pase
    // ya use las ramas de autenticado.
    if (isCompletingSignup && isAuthenticated) {
      setCompletingSignup(false)
    }

    /*
     * Fixture Preview exposes anonymous discovery plus the ordinary auth
     * stack. A fixture session is necessary for QA of protected journeys;
     * reservations, payment and operations remain protected because neither
     * route qualifies as anonymous and the backend still verifies the token.
     *
     * The cold-start landing is the welcome surface, the same one production
     * shows. It used to send the anonymous visitor straight to `/explore`, which
     * made the preview a different app to boot: the whole Maestro suite asserts
     * the welcome screen first, so every flow died on `welcome.login-button` and
     * the lane tested nothing.
     *
     * Exempting the discovery routes was not enough. The tabs navigator resolves
     * `/` to its initial tab (`/explore`) without ever rendering
     * `(tabs)/index.tsx`, so at cold start the only pathname this effect sees is
     * `/explore` — an exempt route — and the landing stayed on discovery. The
     * decision now lives in `previewAnonymousRedirectTarget`, where the auth
     * stack is never interrupted and only an explicit destination (URL on web,
     * intent on native) keeps anonymous discovery; a bare cold start lands on
     * welcome like the store build. It is a pure function with its own test
     * because the first version of this rule shipped as an `||` that read like
     * an exception and acted like "redirect everything" — and the login screen
     * bounced back to welcome on every tap.
     */
    const previewLandingRedirect = previewAnonymousRedirectTarget({
      isPreviewBuild: isIsolatedPreviewBuild,
      isAuthenticated,
      isAuthRoute: isActualAuthRoute,
      isAnonymousPreviewRoute: isAnonymousPreviewRoute(pathname),
      hasExplicitBootDestination: Boolean(webDeepLinkPath || nativeInboundPath),
    })
    if (previewLandingRedirect) {
      redirectTarget = previewLandingRedirect
    } else if (isAuthenticated && biometricChecked && !biometricPassed) {
      redirectTarget = '/(auth)/login'
    } else if (shouldRestoreWebDeepLink && webDeepLinkPath) {
      redirectTarget = webDeepLinkPath
    } else if (
      isAuthenticated &&
      biometricPassed &&
      !accountRestricted &&
      !isCompletingSignup &&
      requiredContactRoute &&
      !isRequiredContactStep &&
      !isPasswordRecoveryStep
    ) {
      redirectTarget = requiredContactRoute
    } else if (
      !isAuthenticated &&
      !isCompletingSignup &&
      ((!inAuthGroup && !inPublicGroup && !isActualAuthRoute) || isBareAuthEntry)
    ) {
      // First-launch / unauthenticated landing on a protected route — show
      // the welcome surface (logo + tagline + register/login CTAs + social
      // auth + trust badges) before dropping into the credential form.
      // Welcome → Login back-arrow works because welcome is the parent.
      // Skip while isCompletingSignup: OTP/password handoff can briefly
      // report isAuthenticated=false before the session cache catches up;
      // bouncing to welcome there is the live "entered code → welcome" bug.
      redirectTarget = '/(auth)/welcome'
    } else if (
      isAuthenticated &&
      biometricPassed &&
      !accountRestricted &&
      isActualAuthRoute &&
      !isCompletingSignup &&
      !isPostSignupAuthStep &&
      !isPasswordRecoveryStep
    ) {
      redirectTarget = '/(tabs)/explore'
    }

    if (redirectTarget) {
      const redirectKey = `${pathname}->${redirectTarget}`
      if (lastAuthRedirectRef.current !== redirectKey) {
        lastAuthRedirectRef.current = redirectKey
        router.replace(redirectTarget as never)
      }
    } else {
      lastAuthRedirectRef.current = null
    }

    if (!authReady) {
      setAuthReady(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- segments/router are unstable refs from Expo Router; including them causes infinite loop
  }, [
    isAuthenticated,
    isInitializing,
    isSessionLoading,
    biometricChecked,
    nativeInitialUrlResolved,
    biometricPassed,
    credentialUnlockNonce,
    isCompletingSignup,
    authReady,
    pathname,
    user?.email,
    user?.phone,
    profile?.email,
    user?.id,
    accountRestricted,
    contactRequirement,
    requiredContactRoute,
    shouldRestoreWebDeepLink,
    webDeepLinkPath,
    isRequiredContactStep,
    isPasswordRecoveryStep,
    isPostSignupAuthStep,
    isActualAuthRoute,
    inAuthGroup,
    inPublicGroup,
    isBareAuthEntry,
  ])

  // Red de seguridad de la guarda de handoff.
  //
  // El efecto de arriba la baja en cuanto hay sesión, que es el caso normal.
  // Pero mientras está arriba bloquea TODA redirección, así que un intento que
  // ni falla ni autentica —una respuesta que nunca llega, una promesa que se
  // pierde— dejaría al usuario en una pantalla muerta para siempre. Antes eso
  // no podía pasar porque el temporizador la bajaba a la fuerza; al quitarlo,
  // el respaldo tiene que ser explícito.
  //
  // Quince segundos: mucho más que cualquier autenticación real, y mucho menos
  // que la paciencia de alguien mirando una pantalla que no responde.
  //
  // PERO bajarla a ciegas era la mitad del doble login: un login real pero
  // lento (red colgada, SecureStore tardando) soltaba la guarda contra "nada",
  // el routing veía no-autenticado y rebotaba a welcome justo cuando la sesión
  // estaba por aterrizar. Antes de soltarla se le pregunta a la sesión viva: si
  // ya existe, se siembra la caché y la guarda se baja CONTRA esa sesión, no
  // contra el vacío. Solo se suelta a secas cuando de verdad no hay sesión en
  // ningún lado.
  useEffect(() => {
    if (isCompletingSignup === false || isAuthenticated) return
    let cancelled = false
    const timer = setTimeout(async () => {
      if (cancelled) return
      try {
        const live = await authService.getSession()
        if (cancelled) return
        if (live?.user) {
          queryClient.setQueryData(authKeys.session(), live)
          setCompletingSignup(false)
          return
        }
      } catch {
        // fall through — suelta la guarda como respaldo, sin trim-back
      }
      if (cancelled) return
      setCompletingSignup(false)
    }, 15_000)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [isCompletingSignup, isAuthenticated, setCompletingSignup])

  const navTheme = resolvedTheme === 'dark' ? DarkTheme : DefaultTheme
  const shouldBlockForUpdate =
    updateGate.status === 'store-required' || updateGate.status === 'error'

  if (shouldBlockForUpdate) {
    return <MandatoryUpdateScreen gate={updateGate} />
  }

  // The gate has several states, but the chrome around them is invariant.
  // Each state used to `return` its own wrapper View plus its own toast host
  // and animated splash. Sibling returns are DIFFERENT JSX positions, so
  // React unmounted and remounted that chrome on every gate transition — and
  // one transition always happens at startup (profilePending true -> false).
  // That restarted the splash mid-animation and tore down the toast host:
  // the flicker. Only the CONTENT varies now; the chrome mounts once.
  let gateBackground: string | undefined
  let gateContent: React.ReactNode

  if (!authReady) {
    // Native SplashScreen has already handed off after the font gate. This
    // frame owns the remaining auth/biometric decision with no worklet,
    // opacity or callback that can stay above an interactive screen.
    gateBackground = LAUNCH_SURFACE
    gateContent = <LaunchHandoffFrame />
  } else if (accountRestricted && profile) {
    gateBackground = gateColors.background
    gateContent = <AccountRestrictedScreen profile={profile} onSignOut={signOut} />
  } else if (profilePending) {
    // `null` here is not "nothing" — the Stack is not rendered in this branch,
    // so the user gets a solid rectangle with no navigator, no controls and no
    // spinner. It is bounded (Supabase aborts at 15s, the query client retries
    // 3x) but that is up to a minute of a screen that looks exactly like a
    // frozen app. GateFallback exists for precisely this; use it.
    gateBackground = gateColors.background
    gateContent = <GateFallback accessibilityLabel={t('common.loading')} testID="gate.profile" />
  } else if (shouldHoldForContactVerification) {
    // Themed, like every other gate state — a hardcoded white gate flashed a
    // full-screen white card at dark-mode users while the hold resolved.
    gateBackground = gateColors.background
    gateContent = (
      <GateFallback
        accessibilityLabel={t('verification.gate_redirecting')}
        testID="gate.contact-verification"
      />
    )
  } else {
    gateBackground = undefined
    gateContent = (
      <NavThemeProvider value={navTheme}>
        <Stack screenOptions={{ headerShown: false, ...stableStackOptions }}>
          <Stack.Screen name="(auth)" />
          <Stack.Screen name="(tabs)" />
          <Stack.Screen name="legal" />
          <Stack.Screen name="split/[token]" />
          <Stack.Screen name="preview" />
          <Stack.Screen name="+not-found" />
        </Stack>
      </NavThemeProvider>
    )
  }

  return (
    <View style={{ flex: 1, backgroundColor: gateBackground ?? rootBackground }}>
      <Animated.View
        style={{
          flex: 1,
          opacity: authReady ? appContentOpacity : 1,
        }}
      >
        {gateContent}
      </Animated.View>
      <ToastContainer />
      {/* OTA: download is owned by useOtaUpdate; apply policy (silent on auth /
          resume, banner while browsing, never mid check-in/checkout) lives in
          OtaUpdateController. checkAutomatically stays NEVER so launch never
          blocks on the network. */}
      {/* Hold OTA silent-apply until the biometric gate resolves. Otherwise a
          pending update on `/` / welcome reloads mid-prompt and the next boot
          asks for the fingerprint again — the "huella dos veces" report. */}
      <OtaUpdateController
        pending={otaUpdate.pending && biometricChecked}
        apply={otaUpdate.apply}
      />
      {/* Recordatorio aplazable del viaje que terminó.
          Se monta como hermana del contenido, igual que el OTA: es una capa
          sobre la app, no un estado de la compuerta de arranque — un `return`
          temprano acá desmontaría el navegador y el toast host, que es el
          defecto que este archivo ya documenta unas líneas más arriba.
          Ella misma decide si se muestra: falla abierta, solo contextual en
          Viajes, respeta las rutas críticas y solo aparece con sesión iniciada. */}
      {isAuthenticated && <MandatoryReviewGate />}
    </View>
  )
}

/**
 * RootLayoutShell — env-misconfiguration safety net.
 *
 * If any required EXPO_PUBLIC_* env var is missing (which
 * happened when the production AAB was built with an incomplete EAS env),
 * we render a static fallback screen INSTEAD of mounting RootLayout.
 * This lets `expo-updates` download an OTA fix in the background and
 * either auto-recover or let the user manually tap "Buscar actualización"
 * to apply it.
 *
 * The check runs BEFORE any provider that touches Supabase, the query
 * client, the router, or anything else that would hit the placeholder
 * env values from lib/env.ts. It must be in a wrapper component (not an
 * early return inside RootLayout) so RootLayout's hooks always run in
 * the same order — Rules of Hooks compliance.
 *
 * `env.isMisconfigured` is computed once at module load and never changes,
 * so this wrapper either always renders ConfigErrorScreen or always
 * renders RootLayout for the lifetime of the JS bundle.
 */
function RootLayoutShell() {
  if (env.isMisconfigured) {
    // Best-effort Sentry signal so production crashes that get a fresh OTA
    // can be correlated with the misconfiguration that caused them. Wrapped
    // in try/catch because Sentry init may itself have failed.
    try {
      Sentry.captureMessage('env_misconfigured: rendering ConfigErrorScreen', {
        level: 'error',
        extra: { configError: env.configError },
      })
    } catch {
      // swallow — last thing we want is the safety screen itself crashing
    }
    if (__DEV__) {
      console.error(
        '[Tago] Rendering ConfigErrorScreen because env is misconfigured:\n' +
          (env.configError ?? '(no detail)'),
      )
    }
    return <ConfigErrorScreen error={env.configError} />
  }
  return <RootLayout />
}

export default withSentry(RootLayoutShell)
